import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchAdminQuestionnaire,
  createAdminQuestionnaire,
  updateAdminQuestionnaire,
  setAdminQuestionnaireStatus,
} from '../../api';
import useNoIndex from '../../hooks/useNoIndex';

function makeId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function defaultOptions() {
  return [
    { value: 0, label: '' },
    { value: 1, label: '' },
    { value: 2, label: '' },
    { value: 3, label: '' },
    { value: 4, label: '' },
  ];
}

function newQuestionnaireState() {
  const dimKey = makeId('dim');
  return {
    slug: '',
    title: '',
    description: '',
    dimensions: [{ key: dimKey, label: '' }],
    questions: [{ id: makeId('q'), dimensionKey: dimKey, text: '', options: defaultOptions() }],
    tiers: [{ name: '', description: '', max: 0 }],
    quickWins: {},
    benchmarkPercent: 40,
    contextFields: { industries: [], employees: [], revenue: [] },
    splash: { heroImage: '', headline: '', description: '', bullets: [], ctaText: '' },
  };
}

function computeMaxScore(questions) {
  return questions.reduce((sum, q) => {
    const values = q.options.map((o) => Number(o.value)).filter((v) => Number.isFinite(v));
    return sum + (values.length ? Math.max(...values) : 0);
  }, 0);
}

function linesToArray(text) {
  return text.split('\n').map((s) => s.trim()).filter(Boolean);
}

function arrayToLines(arr) {
  return (arr || []).join('\n');
}

export default function QuestionnaireEditor() {
  useNoIndex();
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [state, setState] = useState(isNew ? newQuestionnaireState() : null);
  const [status, setStatus] = useState('published');
  const [loadError, setLoadError] = useState(null);
  const [saveErrors, setSaveErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    fetchAdminQuestionnaire(id)
      .then((data) => {
        setState({
          slug: data.slug,
          title: data.title,
          description: data.description || '',
          dimensions: data.dimensions,
          questions: data.questions,
          tiers: data.tiers.map((t) => ({ name: t.name, description: t.description, max: t.max })),
          quickWins: data.quickWins || {},
          benchmarkPercent: data.benchmarkPercent,
          contextFields: data.contextFields || { industries: [], employees: [], revenue: [] },
          splash: {
            heroImage: data.splash?.heroImage || '',
            headline: data.splash?.headline || '',
            description: data.splash?.description || '',
            bullets: data.splash?.bullets || [],
            ctaText: data.splash?.ctaText || '',
          },
        });
        setStatus(data.status);
      })
      .catch((err) => setLoadError(err?.response?.data?.error || 'Could not load questionnaire.'));
  }, [id, isNew]);

  if (loadError) {
    return <div className="page page--wide"><div className="error">{loadError}</div></div>;
  }
  if (!state) {
    return <div className="page page--wide"><p className="muted">Loading…</p></div>;
  }

  const maxScore = computeMaxScore(state.questions);

  // ---- dimension helpers ----
  function updateDimension(key, patch) {
    setState((s) => ({
      ...s,
      dimensions: s.dimensions.map((d) => (d.key === key ? { ...d, ...patch } : d)),
    }));
  }
  function addDimension() {
    const key = makeId('dim');
    setState((s) => ({ ...s, dimensions: [...s.dimensions, { key, label: '' }] }));
  }
  function removeDimension(key) {
    const inUse = state.questions.some((q) => q.dimensionKey === key);
    if (inUse) {
      window.alert('This dimension still has questions assigned to it. Reassign or remove those questions first.');
      return;
    }
    if (!window.confirm('Remove this dimension?')) return;
    setState((s) => ({ ...s, dimensions: s.dimensions.filter((d) => d.key !== key) }));
  }
  function moveDimension(key, dir) {
    setState((s) => {
      const idx = s.dimensions.findIndex((d) => d.key === key);
      const target = idx + dir;
      if (target < 0 || target >= s.dimensions.length) return s;
      const next = [...s.dimensions];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...s, dimensions: next };
    });
  }

  // ---- question helpers ----
  function updateQuestion(id, patch) {
    setState((s) => ({
      ...s,
      questions: s.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));
  }
  function addQuestion(dimensionKey) {
    setState((s) => ({
      ...s,
      questions: [...s.questions, { id: makeId('q'), dimensionKey, text: '', options: defaultOptions() }],
    }));
  }
  function removeQuestion(id) {
    if (!window.confirm('Remove this question?')) return;
    setState((s) => {
      const { [id]: _removed, ...restQuickWins } = s.quickWins;
      return { ...s, questions: s.questions.filter((q) => q.id !== id), quickWins: restQuickWins };
    });
  }
  function moveQuestion(id, dir) {
    setState((s) => {
      const idx = s.questions.findIndex((q) => q.id === id);
      const target = idx + dir;
      if (target < 0 || target >= s.questions.length) return s;
      const next = [...s.questions];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...s, questions: next };
    });
  }
  function updateOption(questionId, optIdx, patch) {
    setState((s) => ({
      ...s,
      questions: s.questions.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.map((o, i) => (i === optIdx ? { ...o, ...patch } : o)) }
          : q
      ),
    }));
  }
  function addOption(questionId) {
    setState((s) => ({
      ...s,
      questions: s.questions.map((q) =>
        q.id === questionId ? { ...q, options: [...q.options, { value: q.options.length, label: '' }] } : q
      ),
    }));
  }
  function removeOption(questionId, optIdx) {
    setState((s) => ({
      ...s,
      questions: s.questions.map((q) =>
        q.id === questionId ? { ...q, options: q.options.filter((_, i) => i !== optIdx) } : q
      ),
    }));
  }
  function updateQuickWin(questionId, text) {
    setState((s) => ({ ...s, quickWins: { ...s.quickWins, [questionId]: text } }));
  }

  // ---- tier helpers ----
  function tiersWithMin() {
    let min = 0;
    return state.tiers.map((t, i) => {
      const row = { ...t, min };
      const isLast = i === state.tiers.length - 1;
      row.max = isLast ? maxScore : Number(t.max);
      min = row.max + 1;
      return row;
    });
  }
  function updateTier(idx, patch) {
    setState((s) => ({ ...s, tiers: s.tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)) }));
  }
  function addTier() {
    setState((s) => ({ ...s, tiers: [...s.tiers, { name: '', description: '', max: maxScore }] }));
  }
  function removeTier(idx) {
    if (state.tiers.length <= 1) return;
    if (!window.confirm('Remove this tier?')) return;
    setState((s) => ({ ...s, tiers: s.tiers.filter((_, i) => i !== idx) }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaveErrors([]);

    const payload = {
      slug: state.slug.trim(),
      title: state.title.trim(),
      description: state.description,
      dimensions: state.dimensions.map((d) => ({ key: d.key, label: d.label })),
      questions: state.questions.map((q) => ({
        id: q.id,
        dimensionKey: q.dimensionKey,
        text: q.text,
        options: q.options.map((o) => ({ value: Number(o.value), label: o.label })),
      })),
      tiers: tiersWithMin().map((t) => ({ min: t.min, max: t.max, name: t.name, description: t.description })),
      quickWins: state.quickWins,
      benchmarkPercent: Number(state.benchmarkPercent),
      contextFields: state.contextFields,
      splash: state.splash,
    };

    try {
      if (isNew) {
        const record = await createAdminQuestionnaire(payload);
        navigate(`/admin/questionnaires/${record.id}`);
      } else {
        await updateAdminQuestionnaire(id, payload);
        navigate('/admin/questionnaires');
      }
    } catch (err) {
      setSaveErrors(err?.response?.data?.errors || [err?.response?.data?.error || 'Save failed.']);
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus() {
    const next = status === 'published' ? 'archived' : 'published';
    try {
      await setAdminQuestionnaireStatus(id, next);
      setStatus(next);
    } catch (err) {
      window.alert(err?.response?.data?.errors?.[0] || 'Could not update status.');
    }
  }

  return (
    <div className="page page--wide">
      <div className="admin-toolbar">
        <button type="button" className="secondary" onClick={() => navigate('/admin/questionnaires')}>
          ← All questionnaires
        </button>
        {!isNew && (
          <button type="button" className="secondary" onClick={toggleStatus}>
            {status === 'published' ? 'Archive' : 'Publish'}
          </button>
        )}
      </div>

      <h1>{isNew ? 'New questionnaire' : 'Edit questionnaire'}</h1>

      {saveErrors.length > 0 && (
        <div className="error">
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {saveErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Basics</h3>
          <div className="field-grid">
            <div className="field">
              <label htmlFor="qb-title">Title</label>
              <input
                id="qb-title"
                type="text"
                value={state.title}
                onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="qb-slug">Slug (used in the public URL: /scorecard/&lt;slug&gt;)</label>
              <input
                id="qb-slug"
                type="text"
                pattern="[a-z0-9-]+"
                value={state.slug}
                onChange={(e) => setState((s) => ({ ...s, slug: e.target.value }))}
                placeholder="e.g. retail-audit"
                required
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="qb-description">Description (internal, optional)</label>
            <textarea
              id="qb-description"
              rows={2}
              value={state.description}
              onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            />
          </div>
          <div className="field">
            <label htmlFor="qb-benchmark">Benchmark % (radar reference line)</label>
            <input
              id="qb-benchmark"
              type="number"
              min="0"
              max="100"
              style={{ maxWidth: '8rem' }}
              value={state.benchmarkPercent}
              onChange={(e) => setState((s) => ({ ...s, benchmarkPercent: e.target.value }))}
            />
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Splash page</h3>
          <p className="muted small" style={{ marginTop: '-0.25rem', marginBottom: '1rem' }}>
            Shown to visitors before the first question, at /scorecard/&lt;slug&gt;. Leave everything
            blank to fall back to the title, no image, and a "See your score" button.
          </p>
          <div className="field">
            <label htmlFor="qb-splash-hero">Hero image URL (optional)</label>
            <input
              id="qb-splash-hero"
              type="text"
              value={state.splash.heroImage}
              onChange={(e) => setState((s) => ({ ...s, splash: { ...s.splash, heroImage: e.target.value } }))}
              placeholder="https://... or /images/your-image.jpg"
            />
          </div>
          <div className="field">
            <label htmlFor="qb-splash-headline">Headline (defaults to the title above)</label>
            <input
              id="qb-splash-headline"
              type="text"
              value={state.splash.headline}
              onChange={(e) => setState((s) => ({ ...s, splash: { ...s.splash, headline: e.target.value } }))}
              placeholder="e.g. How ready is your business for AI?"
            />
          </div>
          <div className="field">
            <label htmlFor="qb-splash-description">Lede paragraph (optional)</label>
            <textarea
              id="qb-splash-description"
              rows={2}
              value={state.splash.description}
              onChange={(e) => setState((s) => ({ ...s, splash: { ...s.splash, description: e.target.value } }))}
            />
          </div>
          <div className="field">
            <label htmlFor="qb-splash-bullets">Bullets (optional, one per line — first sentence is bolded automatically)</label>
            <textarea
              id="qb-splash-bullets"
              rows={4}
              value={arrayToLines(state.splash.bullets)}
              onChange={(e) => setState((s) => ({ ...s, splash: { ...s.splash, bullets: linesToArray(e.target.value) } }))}
              placeholder={'Takes less than 10 minutes. One question at a time.\nA score and a plan. See your tier and top gaps.'}
            />
          </div>
          <div className="field">
            <label htmlFor="qb-splash-cta">Button text (defaults to "See your score")</label>
            <input
              id="qb-splash-cta"
              type="text"
              style={{ maxWidth: '16rem' }}
              value={state.splash.ctaText}
              onChange={(e) => setState((s) => ({ ...s, splash: { ...s.splash, ctaText: e.target.value } }))}
              placeholder="See your score"
            />
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Dimensions</h3>
          <p className="muted small">Max score so far: <strong>{maxScore}</strong></p>
          {state.dimensions.map((dim, idx) => (
            <div key={dim.key} className="qb-row">
              <input
                type="text"
                value={dim.label}
                onChange={(e) => updateDimension(dim.key, { label: e.target.value })}
                placeholder="Dimension label"
                required
                style={{ flex: 1 }}
              />
              <button type="button" className="secondary" onClick={() => moveDimension(dim.key, -1)} disabled={idx === 0}>↑</button>
              <button type="button" className="secondary" onClick={() => moveDimension(dim.key, 1)} disabled={idx === state.dimensions.length - 1}>↓</button>
              <button type="button" className="secondary" onClick={() => removeDimension(dim.key)}>Remove</button>
            </div>
          ))}
          <button type="button" className="secondary" onClick={addDimension} style={{ marginTop: '0.75rem' }}>
            + Add dimension
          </button>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Questions</h3>
          {state.questions.map((q, idx) => (
            <div key={q.id} className="qb-question">
              <div className="qb-row">
                <select
                  value={q.dimensionKey}
                  onChange={(e) => updateQuestion(q.id, { dimensionKey: e.target.value })}
                >
                  {state.dimensions.map((d) => (
                    <option key={d.key} value={d.key}>{d.label || '(untitled dimension)'}</option>
                  ))}
                </select>
                <button type="button" className="secondary" onClick={() => moveQuestion(q.id, -1)} disabled={idx === 0}>↑</button>
                <button type="button" className="secondary" onClick={() => moveQuestion(q.id, 1)} disabled={idx === state.questions.length - 1}>↓</button>
                <button type="button" className="secondary" onClick={() => removeQuestion(q.id)}>Remove</button>
              </div>
              <div className="field">
                <textarea
                  rows={2}
                  value={q.text}
                  onChange={(e) => updateQuestion(q.id, { text: e.target.value })}
                  placeholder="Question text"
                  required
                />
              </div>
              <div className="qb-options">
                {q.options.map((opt, optIdx) => (
                  <div key={optIdx} className="qb-row">
                    <input
                      type="number"
                      value={opt.value}
                      onChange={(e) => updateOption(q.id, optIdx, { value: e.target.value })}
                      style={{ width: '5rem' }}
                    />
                    <input
                      type="text"
                      value={opt.label}
                      onChange={(e) => updateOption(q.id, optIdx, { label: e.target.value })}
                      placeholder="Answer label"
                      required
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => removeOption(q.id, optIdx)}
                      disabled={q.options.length <= 2}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="secondary" onClick={() => addOption(q.id)}>
                  + Add option
                </button>
              </div>
              <div className="field" style={{ marginTop: '0.75rem' }}>
                <label>Quick-win (optional — shown if this is the visitor's weakest question in a weak dimension)</label>
                <textarea
                  rows={2}
                  value={state.quickWins[q.id] || ''}
                  onChange={(e) => updateQuickWin(q.id, e.target.value)}
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            className="secondary"
            onClick={() => addQuestion(state.dimensions[0]?.key)}
            disabled={state.dimensions.length === 0}
          >
            + Add question
          </button>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Tiers</h3>
          <p className="muted small">
            Tiers cover 0–{maxScore}. Each tier's floor is set automatically from the tier above it; only the ceiling (max) is editable, except the last tier which always ends at the max score.
          </p>
          {tiersWithMin().map((t, idx) => {
            const isLast = idx === state.tiers.length - 1;
            return (
              <div key={idx} className="qb-tier">
                <div className="qb-row">
                  <span className="muted small" style={{ minWidth: '4.5rem' }}>{t.min}–{t.max}</span>
                  <input
                    type="text"
                    value={t.name}
                    onChange={(e) => updateTier(idx, { name: e.target.value })}
                    placeholder="Tier name"
                    required
                    style={{ flex: 1 }}
                  />
                  {!isLast && (
                    <input
                      type="number"
                      value={state.tiers[idx].max}
                      onChange={(e) => updateTier(idx, { max: e.target.value })}
                      style={{ width: '6rem' }}
                    />
                  )}
                  <button type="button" className="secondary" onClick={() => removeTier(idx)} disabled={state.tiers.length <= 1}>
                    Remove
                  </button>
                </div>
                <textarea
                  rows={2}
                  value={t.description}
                  onChange={(e) => updateTier(idx, { description: e.target.value })}
                  placeholder="Description shown on the results page"
                />
              </div>
            );
          })}
          <button type="button" className="secondary" onClick={addTier} style={{ marginTop: '0.75rem' }}>
            + Add tier
          </button>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Business context fields (optional, one option per line)</h3>
          <p className="muted small" style={{ marginTop: '-0.25rem', marginBottom: '1rem' }}>
            Leave a box empty to hide that field entirely on this scorecard&apos;s final step.
          </p>
          <div className="field-grid">
            <div className="field">
              <label htmlFor="qb-industries">Industries</label>
              <textarea
                id="qb-industries"
                rows={4}
                value={arrayToLines(state.contextFields.industries)}
                onChange={(e) => setState((s) => ({ ...s, contextFields: { ...s.contextFields, industries: linesToArray(e.target.value) } }))}
              />
            </div>
            <div className="field">
              <label htmlFor="qb-employees">Employee ranges</label>
              <textarea
                id="qb-employees"
                rows={4}
                value={arrayToLines(state.contextFields.employees)}
                onChange={(e) => setState((s) => ({ ...s, contextFields: { ...s.contextFields, employees: linesToArray(e.target.value) } }))}
              />
            </div>
            <div className="field">
              <label htmlFor="qb-revenue">Revenue ranges</label>
              <textarea
                id="qb-revenue"
                rows={4}
                value={arrayToLines(state.contextFields.revenue)}
                onChange={(e) => setState((s) => ({ ...s, contextFields: { ...s.contextFields, revenue: linesToArray(e.target.value) } }))}
              />
            </div>
          </div>
        </div>

        <div className="nav-row">
          <span className="muted small">
            {isNew ? 'New questionnaires are published immediately.' : `Status: ${status}`}
          </span>
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create questionnaire' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
